import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Briefcase, TrendingUp } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import theme from '../theme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BrokerDirectoryPage({ user }) {
  const navigate = useNavigate();
  const [portfolios, setPortfolios] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchActive();
  }, []);

  const fetchActive = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/broker/active`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPortfolios(res.data.portfolios || []);
    } catch (err) {
      toast.error('Failed to load active portfolios');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '32px', color: theme.colors.text.muted }}>Loading...</div>
    );
  }

  return (
    <div data-testid="broker-directory-page">
      <div style={{ marginBottom: '32px' }}>
        <h1
          style={{
            fontFamily: theme.fonts.heading,
            fontSize: '28px',
            fontWeight: '700',
            color: theme.colors.text.primary,
            marginBottom: '8px',
          }}
        >
          Active Portfolios
        </h1>
        <p style={{ color: theme.colors.text.secondary, fontSize: '15px' }}>
          Portfolios you've accepted for brokerage. {portfolios.length > 0 && `(${portfolios.length})`}
        </p>
      </div>

      {portfolios.length === 0 ? (
        <Card style={{ border: `1px solid ${theme.colors.borders}`, borderRadius: theme.radius.card }}>
          <CardContent className="py-16 text-center">
            <Briefcase size={48} style={{ color: theme.colors.text.muted, margin: '0 auto 16px' }} />
            <p
              style={{
                fontFamily: theme.fonts.heading,
                fontSize: '18px',
                fontWeight: '600',
                color: theme.colors.text.primary,
                marginBottom: '8px',
              }}
            >
              No Active Portfolios
            </p>
            <p style={{ color: theme.colors.text.muted, fontSize: '14px', maxWidth: '400px', margin: '0 auto' }}>
              You haven't accepted any portfolio opportunities yet. Head to the Portfolios tab to review available opportunities.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '20px' }}>
          {portfolios.map((p) => (
            <Card
              key={p.id}
              data-testid={`active-portfolio-${p.id}`}
              onClick={() => navigate('/broker/portfolios')}
              style={{
                border: `1px solid ${theme.colors.borders}`,
                borderRadius: theme.radius.card,
                cursor: 'pointer',
                transition: 'border-color 0.2s, transform 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = theme.colors.primary;
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = theme.colors.borders;
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <CardContent className="pt-5 pb-5">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <div>
                    <p
                      style={{
                        fontFamily: theme.fonts.heading,
                        fontSize: '16px',
                        fontWeight: '600',
                        color: theme.colors.text.primary,
                        marginBottom: '4px',
                      }}
                    >
                      {p.name}
                    </p>
                    <p style={{ fontSize: '13px', color: theme.colors.text.muted }}>
                      by {p.creator_name}
                    </p>
                  </div>
                  <span
                    style={{
                      fontSize: '11px',
                      padding: '4px 10px',
                      borderRadius: '999px',
                      background: 'rgba(71, 255, 176, 0.1)',
                      color: '#47FFB0',
                      fontWeight: '600',
                    }}
                  >
                    ACCEPTED
                  </span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginTop: '16px' }}>
                  <div>
                    <p style={{ fontSize: '11px', color: theme.colors.text.muted, marginBottom: '2px' }}>Kickback</p>
                    <p style={{ fontSize: '15px', fontWeight: '600', color: theme.colors.primary }}>
                      {p.kickback_percent}%
                    </p>
                  </div>
                  <div>
                    <p style={{ fontSize: '11px', color: theme.colors.text.muted, marginBottom: '2px' }}>Units</p>
                    <p style={{ fontSize: '15px', fontWeight: '600', color: theme.colors.text.primary }}>
                      {p.total_units}
                    </p>
                  </div>
                  <div>
                    <p style={{ fontSize: '11px', color: theme.colors.text.muted, marginBottom: '2px' }}>Category</p>
                    <p style={{ fontSize: '14px', fontWeight: '500', color: theme.colors.text.secondary, textTransform: 'capitalize' }}>
                      {p.category}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
